import type { Chamber } from '../../types/lobbyDay'

export type DistrictUnitType =
  | 'StateHouseDistrict'
  | 'StateSenateDistrict'
  | 'CongressionalDistrict'

export interface GeoDistrict {
  code: string
  name: string
}

const UNIT_TYPE_BY_CHAMBER: Record<Chamber, DistrictUnitType | null> = {
  StateHouse: 'StateHouseDistrict',
  StateSenate: 'StateSenateDistrict',
  USHouse: 'CongressionalDistrict',
  // Whole-state constituency — there is no district, and the form suppresses the field.
  USSenate: null,
}

/** Chamber -> GeoUnitType. NOTE the enum member is `USHouse`, not `Congressional`; the Wave 3
 *  spec's table names it wrongly and the identifier does not exist. */
export function districtUnitTypeFor(chamber: Chamber): DistrictUnitType | null {
  return UNIT_TYPE_BY_CHAMBER[chamber] ?? null
}

const DEFAULT_LIMIT = 50

/** Substring match over name and code, for the combobox's own list. */
export function filterDistricts(
  catalog: GeoDistrict[], query: string, limit = DEFAULT_LIMIT,
): GeoDistrict[] {
  const q = query.trim().toLowerCase()
  const rows = q
    ? catalog.filter((d) => d.name.toLowerCase().includes(q) || d.code.toLowerCase().includes(q))
    : catalog
  return rows.slice(0, limit)
}

const tokens = (s: string) =>
  s.toLowerCase().split(/[^a-z0-9]+/).filter((t) => t.length > 0)

/**
 * "Did you mean…" suggestions for a district that matched nobody. **Display only** — whether a
 * district really matches is decided server-side by the count probe, which routes through
 * DistrictMatcher. Do not grow this into a client mirror of Normalize.
 */
export function nearMisses(catalog: GeoDistrict[], raw: string, limit = 5): GeoDistrict[] {
  const wanted = tokens(raw)
  if (wanted.length === 0 || catalog.length === 0) return []

  const digits = wanted.filter((t) => /^\d+$/.test(t))
  const words = wanted.filter((t) => !/^\d+$/.test(t) && t.length > 2)

  const scored = catalog
    .map((d) => {
      const have = new Set(tokens(`${d.name} ${d.code}`))
      const digitHits = digits.filter((t) => have.has(t) || have.has(t.replace(/^0+/, ''))).length
      const wordHits = words.filter((t) => have.has(t)).length
      return { district: d, score: digitHits * 2 + wordHits }
    })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)

  return scored.slice(0, limit).map((s) => s.district)
}
